import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/dashboards/News.css';
import './assets/css/dashboards/Pricing.css';
import './assets/css/dashboards/Settings.css';
import './assets/css/dashboards/UserDashboard.css';
import './assets/css/general/App.css';
import './assets/css/general/Buttons.css';
import './assets/css/general/Content.css';
import './assets/css/general/Extra.css';
import './assets/css/general/Forms.css';
import './assets/css/general/Menu.css';
import './assets/css/general/Modal.css';
import './assets/css/general/Utilities.css';
import './assets/css/Inputs.css';
import './assets/css/Leaflet.css';

// core styles are required for all packages
import '@mantine/core/styles.css';
// other css files are required only if
// you are using components from the corresponding package
// import '@mantine/dates/styles.css';
// import '@mantine/dropzone/styles.css';
// import '@mantine/code-highlight/styles.css';

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { ReturnPropsAPIGetUserLoginResponse } from './api/APIUsers';
import { APIGetUserSubscriptionPlanResponse, ReturnPropsAPIGetUserSubscriptionPlanResponse } from './api/Common';
import { useAppDispatch, useAppSelector } from './AppHooks';
import useScript from './assets/components/Common';
import { SubscriptionPlan } from './assets/components/data/SubscriptionData';
import { Button } from './assets/components/elements/Button';
import Header from './assets/components/elements/Header';
import Loading from './assets/components/elements/Loading';
import CookieConsent from './assets/components/elements/CookieConsent';
import FooterV2 from './assets/components/elements/FooterV2';
import ContactForm from './assets/components/forms/ContactForm';
import LoginForm from './assets/components/forms/LoginForm';
import LoginVerifyOTPForm from './assets/components/forms/LoginVerifyOTPForm';
import PrivateRoute from './assets/components/privateRoute';
import { setSubscriptionStatus } from './slices/UserSlice';
import { IUserProps } from './views/Dashboard';
import RouteNotFound from './views/RouteNotFound';
import RegisterForm from './assets/components/forms/RegisterForm';
import { 
  initializeIfConsented, 
  trackPageView, 
  AnalyticsConsent,
  getAnalyticsConsent 
} from './utils/analytics';

const Home = lazy(() => import('./views/Home'));
const AboutV2 = lazy(() => import('./views/AboutV2'));
const ServicesV2 = lazy(() => import('./views/ServicesV2'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const DashboardV2 = lazy(() => import('./views/DashboardV2'));
const GeneralDashboard = lazy(() => import('./views/GeneralDashboard'));
const GenericOkayForm = lazy(() => import('./assets/components/forms/GenericOkayForm'));
const Pricing = lazy(() => import('./views/Pricing'));
const Weather = lazy(() => import('./views/Weather'));
const Settings = lazy(() => import('./views/Settings'));
const SettingsAccountInformation = lazy(() => import('./assets/components/elements/dashboardElements/SettingsAccountInformation'));
const SettingsPreferences = lazy(() => import('./assets/components/elements/dashboardElements/SettingsPreferences'));
const SettingsNotifications = lazy(() => import('./assets/components/elements/dashboardElements/SettingsNotifications'));
const SettingsSecurity = lazy(() => import('./assets/components/elements/dashboardElements/SettingsSecurity'));
const SettingsPrivacyAndData = lazy(() => import('./assets/components/elements/dashboardElements/SettingsPrivacyAndData'));
const SettingsHelp = lazy(() => import('./assets/components/elements/dashboardElements/SettingsHelp'));
const SettingsFeedback = lazy(() => import('./assets/components/elements/dashboardElements/SettingsFeedback'));
const SettingsAbout = lazy(() => import('./assets/components/elements/dashboardElements/SettingsAbout'));
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy'))
const PasswordPreResetForm = lazy(() => import('./assets/components/forms/PasswordPreResetForm'));
const PasswordResetOTPForm = lazy(() => import('./assets/components/forms/PasswordResetOTPForm'));
const PasswordResetForm = lazy(() => import('./assets/components/forms/PasswordResetForm'));
const Modal = lazy(() => import('./assets/components/elements/Modal'));
const FeedbackForm = lazy(() => import('./assets/components/forms/FeedbackForm'));
const ActionPanel = lazy(() => import('./assets/components/utilities/ActionPanel'));
const ThemeManager = lazy(() => import('./views/ThemeManager'));

// https://react-icons.github.io/react-icons

function App() {
	const dispatch = useAppDispatch();
	const userPlan = useAppSelector(state => state.users.subscriptionPlan);
	const [isContactFormOpen, setContactFormOpen] = useState(false);
	const [isContactSubmitFormOpen, setContactSubmitFormOpen] = useState(false);
	const [isFeedbackFormOpen, setFeedbackFormOpen] = useState(false);
	const [isFeedbackSubmitFormOpen, setFeedbackSubmitFormOpen] = useState(false);
	const [isFooterShowing, setFooterShowing] = useState(false);
	const [apiLoginData, setAPILoginData] = useState<ReturnPropsAPIGetUserLoginResponse>(null);
	const isLoggedIn = useAppSelector(state => state.users.isLoggedIn);
	const appContentRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const isLoginRoute = location.pathname === '/login' || location.pathname === '/login/verify';
	const isSettingsRoute = location.pathname.startsWith('/settings');
	const contentOffsetClass = isLoginRoute || location.pathname === '/'
		? ''
		: isSettingsRoute
			? 'content-under-nav'
			: 'content-under-logo';

	// Check if current page is a V2 page
	const isV2Page = () => {
		const v2Routes = ['/', '/about', '/services', '/about-v2', '/services-v2', '/dashboard-v2', '/login', '/login/verify'];
		return v2Routes.some(route => location.pathname === route || location.pathname.startsWith(`${route}/`))
			|| location.pathname.includes('-v2')
			|| location.pathname.includes('/v2');
	};

	useEffect(() => {		
		// Reset footer variables and its showing state when location changes
		if (footerRef.current && footerRef.current.style) {
			footerRef.current.style.transition = 'bottom .5s';
			footerRef.current.style.bottom = '-40%';
		}

		// Reset scroll position to top of page
		if (appContentRef.current) {
			appContentRef.current.scrollTo(0, 0);
		}

		setFooterShowing(false);
		
		// Track page views for analytics
		const consent = getAnalyticsConsent();
		if (consent === AnalyticsConsent.GRANTED) {
			trackPageView(document.title, window.location.href);
		}
	}, [location]);

	const handleContainerScroll = (event: React.UIEvent<HTMLDivElement> | Event) => {
		try {		
			const container = appContentRef.current;
			
			if (container && footerRef.current) {
				const { scrollTop, scrollHeight, clientHeight } = container;

				if (scrollTop + clientHeight >= scrollHeight) {
					// Reached bottom - show footer with smooth transition
					setFooterShowing(true);
					footerRef.current.style.transition = 'bottom 0.5s ease-out';
					if (footerRef.current.style.bottom === '-40%' || footerRef.current.style.bottom === '') {
						footerRef.current.style.bottom = '-35%';
					}
				}
			}
		} catch (error) {
			console.log("Error in handle container scroll: ", error);
		}
	};

	const handleGeneralScroll = (event: React.WheelEvent<HTMLDivElement> | WheelEvent) => {
		try {
			if (isFooterShowing && footerRef.current) {
				// Gradually reveal footer as user scrolls down with smooth transitions
				let currentBottom = footerRef.current.style.bottom || '-40%';
				let currentBottomValue = parseInt(currentBottom);
				
				// Use CSS transition (already set in CSS), no need to set it on every event
				if (event.deltaY > 0) {
					// Scrolling down - reveal more footer smoothly
					if (currentBottomValue < 0) {
						const newBottom = Math.min(currentBottomValue + 2, 0); // Increment by 2%, limit to 0%
						footerRef.current.style.bottom = newBottom + '%';
					}
				} else if (event.deltaY < 0) {
					// Scrolling up - hide footer immediately on first scroll up
					footerRef.current.style.bottom = '-40%';
					setFooterShowing(false);
				}
			}
		} catch (error) {
			console.log("Error in handle general scroll: ", error);
		}
	}

	useScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css')
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');  // we store our css with other css files and load js here
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick.min.css');
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick-theme.min.css');

	// Initialize theme function - reusable
	const initializeTheme = () => {
		// Check if user is authenticated
		const currentUser = localStorage.getItem('user');
		if (!currentUser) {
			// Remove any theme classes for unauthenticated users
			document.body.classList.remove('theme-light', 'theme-dark');
			return;
		}
		
		const storedTheme = localStorage.getItem('app-theme') as 'light' | 'dark' | 'auto' | null;
		// Default to 'light' for logged-in users if no theme is stored
		const theme = storedTheme || 'light';
		
		if (theme === 'auto') {
			const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
			const systemTheme = mediaQuery.matches ? 'dark' : 'light';
			document.body.classList.remove('theme-light', 'theme-dark');
			document.body.classList.add(`theme-${systemTheme}`);
			
			// Listen for system theme changes
			const handler = (e: MediaQueryListEvent) => {
				const newTheme = e.matches ? 'dark' : 'light';
				document.body.classList.remove('theme-light', 'theme-dark');
				document.body.classList.add(`theme-${newTheme}`);
			};
			mediaQuery.addEventListener('change', handler);
		} else {
			document.body.classList.remove('theme-light', 'theme-dark');
			document.body.classList.add(`theme-${theme}`);
		}
	};

	useEffect(() => {
		// Scroll certain amounts from current position 
		window.scrollBy({ 
			top: 10, // could be negative value
			left: 0, 
			behavior: 'smooth' 
		});
		
		// Initialize Google Analytics if user has already consented
		initializeIfConsented();
		
		// Initialize theme from localStorage on mount
		initializeTheme();
	}, []);

	// Re-apply theme when login state changes
	useEffect(() => {
		initializeTheme();
	}, [isLoggedIn]);

	// Also listen for localStorage changes (cross-tab) and periodically check for user login
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === 'user') {
				// User was added or removed - re-initialize theme
				initializeTheme();
			}
		};

		// Listen for storage events (from other tabs/windows)
		window.addEventListener('storage', handleStorageChange);

		// Check for user login periodically (helps catch cases where user is set
		// in localStorage before Redux state updates)
		let checkCount = 0;
		const maxChecks = 20; // Check for 2 seconds (20 * 100ms)
		const checkUserInterval = setInterval(() => {
			const currentUser = localStorage.getItem('user');
			const hasUser = currentUser !== null;
			
			// If user exists in localStorage but isn't logged in yet, apply theme anyway
			if (hasUser) {
				initializeTheme();
			}
			
			checkCount++;
			if (checkCount >= maxChecks || (hasUser && isLoggedIn)) {
				clearInterval(checkUserInterval);
			}
		}, 100);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			clearInterval(checkUserInterval);
		};
	}, [isLoggedIn]);

	useEffect(() => {
        // console.log("User plan: ", userPlan);

    }, [userPlan])

	useEffect(() => {
		const fetchUserPlan = async () => {
			// Get user email
			let currentUser: IUserProps = JSON.parse(localStorage.getItem('user'));
			// console.log("User localstorage data: ", currentUser);

			// Check for subscription plan
			if (currentUser != null) {
				let subscriptionResponse: ReturnPropsAPIGetUserSubscriptionPlanResponse = await APIGetUserSubscriptionPlanResponse(currentUser.email);

				// Set user plan from api if api supplies one, else assume free plan
				dispatch(setSubscriptionStatus(subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free));
			}
		}
		fetchUserPlan();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Periodically check if user has a subscription plan.
	useEffect(() => {
		const interval = setInterval(async () => {
			// Get user email
			let currentUser: IUserProps = JSON.parse(localStorage.getItem('user'));
			// console.log("User localstorage data: ", currentUser);			

			// Check for subscription plan
			if (currentUser != null) {
				let subscriptionResponse: ReturnPropsAPIGetUserSubscriptionPlanResponse = await APIGetUserSubscriptionPlanResponse(currentUser.email);

				// Set user plan from api if api supplies one, else assume free plan
				dispatch(setSubscriptionStatus(subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free));
				// console.log("User plan: ", subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free);
				
			}
		}, 600000);  // 10 minutes * 60 seconds * 1000 milliseconds = 600000 milliseconds
		return () => {
			clearInterval(interval);
		};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

	return (
		<div 
			className={`App ${isV2Page() ? 'v2-page' : ''}`}
			// onContextMenu={(e) => {
            // 	e.preventDefault(); // prevent the default behaviour when right clicked
            // 	console.log("Right Click");
			// 	// TODO: Implement right click menu with redux options and more.	
          	// }}
		  >
			<Suspense fallback={<Loading />}>
				<Header />
				<div 
					className={`app-content ${isLoginRoute ? 'login-page-content' : ''}`} 
					ref={appContentRef}
					onScroll={handleContainerScroll} 
					onWheel={handleGeneralScroll}
				>
					<ActionPanel />
					<div className={`route-view ${contentOffsetClass}`}>
						<Routes>
							<Route path='/' element={ <Home /> }/>
							<Route path='/about' element={ <AboutV2 /> }/>
							<Route path='/about-v2' element={<AboutV2 />} />
							<Route path='/services' element={ <ServicesV2 /> }/>
							<Route path='/services-v2' element={<ServicesV2 />} />
							<Route path='/dashboard-v2' element={<PrivateRoute><DashboardV2 /></PrivateRoute>} />
							<Route path='/dashboard' element={<PrivateRoute><Dashboard userPlan={userPlan}/></PrivateRoute>}>
								<Route path='' element={<GeneralDashboard />}/>
							</Route>
							{/* <Route path='/admin' element={ <Admin /> } /> */}
							<Route path='/login' element={ <LoginForm userPlan={userPlan} apiLoginData={apiLoginData} setApiLoginData={setAPILoginData} /> } />
							<Route path='/login/verify' element={ <LoginVerifyOTPForm userPlan={userPlan} apiLoginData={apiLoginData} /> } />
							<Route path='/privacy-policy' element={ <PrivacyPolicy /> } />
							{/* <Route path='/pricing' element={ <Pricing /> } /> */}
							<Route path='/register' element={ <RegisterForm /> } />
							<Route path='/pre-password-reset' element={ <PasswordPreResetForm /> } />
							<Route path='/password-reset-otp' element={ <PasswordResetOTPForm /> } />
							<Route path='/password-reset' element={ <PasswordResetForm /> } />
							{/* <Route path='/news' element={<PrivateRoute><News /></PrivateRoute>} /> */}
                                                  <Route path='/settings' element={ <Settings /> } >
                                                          <Route index element={ <SettingsAccountInformation /> } />
                                                          <Route path='account-information' element={ <SettingsAccountInformation /> } />
                                                          <Route path='preferences' element={ <SettingsPreferences /> } />
                                                          {/* <Route path='appearance' element={ <SettingsAppearance /> } /> */}
                                                          <Route path='notifications' element={ <SettingsNotifications /> } />
                                                          <Route path='security' element={ <SettingsSecurity /> } />
                                                          <Route path='privacy-and-data' element={ <SettingsPrivacyAndData /> } />
                                                          <Route path='help' element={ <SettingsHelp /> } />
                                                          <Route path='feedback' element={ <SettingsFeedback /> } />
                                                          <Route path='about' element={ <SettingsAbout /> } />
                                                  </Route>
                                                  <Route path='/theme-manager' element={<PrivateRoute><ThemeManager /></PrivateRoute>} />
							  <Route path='/experimental' element={<PrivateRoute><ThemeManager /></PrivateRoute>} />
                                                  {/* Look closely below. The dashboards are children of the user dashboard. */}
                                                  <Route path='/weather' element={<PrivateRoute><Weather /></PrivateRoute>} />
                                                  <Route path='*' element={ <RouteNotFound /> } />
                                          </Routes>
					</div>
                                  </div>
				
				{/* Original Footer for non-V2 pages - outside scrollable content */}
				{!isV2Page() && (
					<footer 
						className='app-footer' 
						style={{
							display: (isFooterShowing && !isLoginRoute) ? 'flex' : 'none',
							bottom: '-40%'
						}} 
						ref={footerRef}
					>
						<div className='social-container'></div>
						<div className='business-contact'>
							<div className='contact-container'>
								<Button
									props={{
										text: "Contact Us",
										onClick: () => {setContactFormOpen(!isContactFormOpen)},
										classes: 'dark'
									}}
								/>
							</div>
							{isLoggedIn && (
								<div className='feedback-container'>
									<Button
										props={{
											text: "Give Feedback",
											onClick: () => {setFeedbackFormOpen(!isFeedbackFormOpen)},
											classes: 'dark'
										}}
									/>
								</div>
							)}
							<a href='tel:314-964-6438'><i className="fa-solid fa-phone"></i> 314-964-6438</a>
							<a href='mailto:brenton.atchison@lostinedrone.com'><i className="fa-solid fa-envelope"></i> brenton.atchison@lostinedrone.com</a>
							<span><i className="fa-solid fa-location-dot"></i> 12645 Olive Blvd 2nd Floor St. Louis, MO, 63141</span>
						</div>
						<div className='business-information'>
							<div className='business-links'>
								<Link to='/about'>About</Link>| 
								<Link to='/privacy-policy'>Privacy Policy</Link>| 
								<a href='https://form.jotform.com/243178125183052' target='_blank' rel='noreferrer'>Request Refund</a>
							</div>
							<div className='footers-footer'>
								<span className='footer-license-and-insured'>Lostine Drone Co. is FAA Part 107 licensed and insured up to $1 Million</span>
								<span>© 2024 Lostine Drone Co</span>
							</div>
						</div>
					</footer>
				)}
				
				{/* Enhanced Footer V2 for V2 pages - outside scrollable content */}
				{isV2Page() && !isLoginRoute && (
					<FooterV2
						isLoggedIn={isLoggedIn}
						onContactClick={() => setContactFormOpen(!isContactFormOpen)}
						onFeedbackClick={() => setFeedbackFormOpen(!isFeedbackFormOpen)}
					/>
				)}
				
				{isContactSubmitFormOpen && (
					<GenericOkayForm title="Submitted!" description="Your contact request has been submitted." isShowingGenericOkayForm={setContactSubmitFormOpen}/>
				)}
				{isFeedbackSubmitFormOpen && (
					<GenericOkayForm title="Submitted!" description="Your feedback has been submitted." isShowingGenericOkayForm={setFeedbackSubmitFormOpen}/>
				)}
				{isContactFormOpen && (
					<>
						<Modal
							title='Contact Us'
							textInfo='Please provide your contact information and a message below.' 
							classes='contact-modal'
							form={<ContactForm isContactSubmitModal={setContactSubmitFormOpen} isModelShowing={setContactFormOpen} />}
						/>
					</>
				)}
				{isFeedbackFormOpen && (
					<>
						<Modal
							title='Feedback'
							textInfo='Please provide feedback below.' 
							classes='feedback-modal'
							form={<FeedbackForm isFeedbackSubmitModal={setFeedbackSubmitFormOpen} isModelShowing={setFeedbackFormOpen} />}
						/>
					</>
				)}
				
				{/* Cookie Consent Banner */}
				<CookieConsent />
			</Suspense>
		</div>
	);
}

export default App;
